import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
// Material UI
import Box from '@material-ui/core/Box';
import withTheme from '@material-ui/styles/withTheme';
import List from '@material-ui/core/List';
import CircularProgress from '@material-ui/core/CircularProgress';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';

import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import EmailIcon from '@material-ui/icons/Email';
import UpdateIcon from '@material-ui/icons/Update';

import ShowingCode from '../components/ShowingCode';
import { Label, Value } from './CardCommon';

import { DATA_TESTIDS } from '~/lib/utils/constant/data-testId';

const StatusListItem = withTheme(styled(ListItem)`
  ${(props) => {
    return css`
      line-height: 28px;
      cursor: inherit;
      opacity: ${{
        active: 1,
        failed: 1,
        completed: 0.3,
        finished: 1,
        pending: 0.15,
      }[props.status]};
    `;
  }};
`);

const StatusListIcon = styled(ListItemIcon)`
  &&& {
    text-align: right;
    margin-left: 16px;
    min-width: 24px;
    position: relative;
  }
`;

export default function ShowingStatus({ showing }) {
  if (!showing) {
    return null;
  }

  const isTerminalStatus = ['Pending', 'Canceled', 'Completed', 'Past'].includes(showing.status);
  const isSuccessfulStatus = ['Pending', 'Completed'].includes(showing.status);
  const processingStatus = showing.status === 'Failed' ? 'failed' : 'active';

  return (
    <Fragment>
      {isTerminalStatus && (
        <Box width="100%" pl={1} pr={1} flex={1}>
          <Value>
            <Label>Status</Label>
            <StatusItem
              label={showing.status === 'Pending' ? 'Confirmed' : showing.status}
              status={isSuccessfulStatus ? 'finished' : 'pending'}
              data-testid={DATA_TESTIDS.SELFSHOW.SHOW_STATUS}
            />
            {showing.status === 'Pending' && !showing.code ? (
              <Fragment>
                <StatusItem
                  status="info"
                  label="Your tour code will be available a few minutes before your showing."
                  data-testid={DATA_TESTIDS.SELFSHOW.CODE_AVAILABILITY}
                />
                <StatusItem
                  status="info"
                  label="You will receive your tour code via e-mail and/or text message, according to your communication preferences."
                  JobStatusIcon={EmailIcon}
                  data-testid={DATA_TESTIDS.SELFSHOW.EMAIL_INFO}
                />
                <StatusItem
                  status="info"
                  label="You can return to this page at the time of your tour to view your code."
                  JobStatusIcon={UpdateIcon}
                  data-testid={DATA_TESTIDS.SELFSHOW.RETURN_TO_VIEW_INFO}
                />
              </Fragment>
            ) : null}
          </Value>
        </Box>
      )}

      {isSuccessfulStatus && showing.code && (
        <Box width="100%" pl={1} pr={1} flex={1}>
          <ShowingCode showing_id={showing.id} code={showing.code} />
        </Box>
      )}

      {!isTerminalStatus && (
        <List>
          <StatusItem label="Preparing Self-Tour" status={processingStatus} />
        </List>
      )}
    </Fragment>
  );
}

function StatusItem({
  label,
  status,
  secondary = false,
  successIcon = CheckCircleIcon,
  JobStatusIcon = null,
  ...rest
}) {
  let jobStatusColor = 'disabled';
  let jobTypeVariant = 'body';

  if (['completed', 'finished'].includes(status)) {
    jobStatusColor = 'primary';
    jobTypeVariant = status === 'finished' ? 'h5' : 'body';
    if (!JobStatusIcon) {
      JobStatusIcon = successIcon;
    }
  } else if (status === 'active') {
    jobStatusColor = 'primary';
    if (!JobStatusIcon) {
      JobStatusIcon = CircularProgress;
    }
  } else if (status === 'failed') {
    jobStatusColor = 'error';
    if (!JobStatusIcon) {
      JobStatusIcon = ErrorOutlineIcon;
    }
  } else if (status === 'info') {
    jobStatusColor = 'info';
    jobTypeVariant = 'body2';
    if (!JobStatusIcon) {
      JobStatusIcon = InfoIcon;
    }
  }

  if (!JobStatusIcon) {
    JobStatusIcon = MoreHorizIcon;
  }

  return (
    <StatusListItem status={status} color={jobStatusColor} {...rest}>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          color: jobStatusColor,
          variant: jobTypeVariant,
        }}
        secondary={secondary || (status === 'failed' ? 'Failed. Please contact us.' : '')}
        secondaryTypographyProps={{ color: jobStatusColor }}
      />
      <StatusListIcon>
        <JobStatusIcon color={jobStatusColor} size={24} />
      </StatusListIcon>
    </StatusListItem>
  );
}
